import { Divider } from '@material-ui/core';
import React from 'react';
import { useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { ICampgroundProps } from '@/components/route/campground/campgroundTypes';
import ErrorPageComponent from '@/components/route/error/ErrorPage/ErrorPage';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { stripHTML } from '@/utility/stripHtml';

import { About } from './About';
import { NearbyCampgrounds } from './NearbyCampgrounds';
import { NearbyParks } from './NearbyParks';
import { NearbyRVs } from './NearbyRVs';
import { PageHero } from './PageHero';

export const CampgroundPage = ({ metadata, error, images, fallback }: ICampgroundProps) => {
  const intl = useIntl();

  const { campground_info, title, breadcrumbs, canonical_url, nearby, introduction, location } =
    metadata || {};

  if (error) {
    return <ErrorPageComponent statusCode={error.statusCode} message={error.message} />;
  }

  const pageTitle = campground_info?.address
    ? intl.formatMessage(
        {
          defaultMessage: `Take a trip to {campground} - Outdoorsy in {locality}`,
          id: 'FFjGC2',
        },
        {
          campground: title,
          locality: `${campground_info.address.city}, ${campground_info.address.state}`,
        },
      )
    : intl.formatMessage(
        {
          defaultMessage: `Take a trip to {campground} - Outdoorsy`,
          id: 'xESh3s',
        },
        {
          campground: title,
        },
      );

  const description = stripHTML(campground_info?.description || introduction);
  const pageSEODescription = description.slice(0, 200) + (description.length > 200 ? '...' : '');

  return (
    <>
      <HeadMeta
        title={pageTitle}
        description={pageSEODescription}
        canonicalUrl={canonical_url}
        noindex={!!fallback}
      />

      <PageHero
        title={pageTitle}
        description={pageSEODescription}
        breadcrumbs={breadcrumbs}
        images={images}
      />

      <section className="py-10 md:py-16">
        <MaxWidth className="mx-auto">
          <About
            description={description}
            attributes={campground_info?.attributes}
            breadcrumbs={breadcrumbs}
            title={title}
            address={campground_info?.address}
          />
        </MaxWidth>
      </section>

      <MaxWidth className="mx-auto">
        <Divider />
      </MaxWidth>

      <section className="py-10 md:py-16">
        <MaxWidth className="mx-auto">
          <NearbyRVs address={campground_info?.address.city} />
        </MaxWidth>
      </section>

      <section className="py-10 md:py-16 bg-canvas-100">
        <MaxWidth className="mx-auto">
          <NearbyParks
            location={location}
            activities={campground_info?.activities}
            address={campground_info?.address}
          />
        </MaxWidth>
      </section>

      <section className="py-10 md:py-16">
        <MaxWidth className="mx-auto">
          <NearbyCampgrounds campgrounds={nearby.campground} />
        </MaxWidth>
      </section>
    </>
  );
};
